import { Component, EventEmitter, Input, Output } from "@angular/core";
import { FileGalleryDialogComponent, FileGalleryDialogData, FileService, UtilDialogService } from "@core";
import { AccountImage } from "@models";
import { ImageFns, ProxImage } from "@utils";
import { Entity, EntityState } from "breeze-client";
import * as _ from "lodash-es";
import { AccountDbQueryService } from "./account-db-query.service";
import { AccountDbSaveService } from "./account-db-save.service";

interface RelatedAccountImage extends Entity {
  accountImageId: string;
  accountImage: AccountImage;
  isPrimary: boolean;
}

/** Handles image uploads and interactions with image gallery */
@Component({
  selector: 'prox-account-image-handler',
  template: `<div>
  <button *ngIf="!hideButton" (click)="openFileViewDialog()" pButton type="button" icon="pi pi-images" label="Add / View {{ imageCount }} Images"
    class="p-button-sm p-button-raised mb-2"></button>
</div>
`,
  standalone: false
})
export class AccountImageHandlerComponent {
  /** Linking entity collection on the parent entity, e.g. manufacturer.manufacturerImages */
  @Input() oldImages!: ProxImage[];
  @Input() accountId?: string;
  /** Whether to hide the openFileDialog button.  If true, then the parent component must call openFileDialog() directly. */
  @Input() hideButton = false;
  /** Create the join entity between AccountImage and parent entity.  Example implementation:
   * this.dbSaveService.createEntity(ManufacturerImage, {
   *   manufacturerId: this.manufacturer.id,
   *   accountImageId: event.accountImageId,
   *   isPrimary: event.isPrimary
   * });
   */
  @Output() createJoinEntity = new EventEmitter<{ accountImageId: string, isPrimary: boolean }>();

  isChanged = false;
  newImages: ProxImage[] = [];

  constructor(
    private dbQueryService: AccountDbQueryService,
    private dbSaveService: AccountDbSaveService,
    private dialogService: UtilDialogService,
    private fileService: FileService) {
  }

  /** Add files chosen by the file control to the newImages collection */
  // addFiles(event: Event) {
  //   if (event.target && (event.target as HTMLInputElement).files) {
  //     const files = (event.target as HTMLInputElement).files as FileList;
  //     for (let i = 0; i < files?.length; i++) {

  //       ImageFns.resizeFileToStandard(files[i]).then(proxImage => {
  //         this.newImages.push(proxImage);
  //       })

  //     }
  //     this.isChanged = true;
  //   }
  // }

  /** Images that are not deleted or detached */
  get filteredNewImages(): ReadonlyArray<ProxImage> {
    return this.newImages.filter(
      (x) =>
        !x.entityAspect ||
        (x.entityAspect.entityState != EntityState.Deleted &&
          x.entityAspect.entityState != EntityState.Detached)
    );
  }

  get imageCount(): number {
    // files just added + files already attached 
    return this.filteredNewImages.length + this.oldImages.length;
  }

  async openFileViewDialog(enableSave = false) {
    // if (this.oldImages.some(pti => pti.accountImage == null)) {
    //   // load the AccountImages
    //   const accountImageIds = this.oldImages.map(pti => pti.accountImageId);
    //   await this.dbQueryService.getAccountImages(...accountImageIds);
    // }
    // const images = this.oldImages.map(pti => {
    //   const image = pti.accountImage as ProxImage;
    //   image.isPrimary = pti.isPrimary;
    //   return image;
    // });
    const images = this.oldImages;

    return await this.dialogService.create<FileGalleryDialogData>(FileGalleryDialogComponent,
      {
        newImages: this.newImages,
        oldImages: images,
        onDelete: this.deleteFile.bind(this),
        onChange: this.fileChange.bind(this),
        enableSave,
      },
      FileGalleryDialogComponent.config);
  }

  /** Upload the files to the server, and attach the resulting AccountImages to ProductTypeImages */
  // async attachFilesOld() {
  //   const accountImages = await this.fileService.uploadAccountImages(this.accountId, this.newImages);

  //   for (let i = 0; i < accountImages.length; i++) {
  //     const si = accountImages[i];
  //     this.createJoinEntity.emit({ accountImageId: si.id, isPrimary: this.newImages[i].isPrimary || false });
  //   }
  //   this.newImages = [];
  //   this.isChanged = false;
  // }

  /** Create AccountImages from the ProxImages, and create join entities for each */
  async attachFiles() {
    for (const img of this.filteredNewImages) {
      const si = await this.makeAccountImage(img);
      const re = this.createJoinEntity.emit({
        accountImageId: si.id,
        isPrimary: img.isPrimary || false,
      });
    }

    this.newImages = [];
  }


  /** When an image is deleted from the gallery dialog, mark it deleted here */
  private deleteFile(image: ProxImage) {
    if (image instanceof AccountImage) {
      // const pti = this.oldImages.find(pti => pti.accountImage == image);
      // if (pti) {
      //   pti.entityAspect.setDeleted();
      // }
      image.entityAspect.setDeleted();
      _.remove(this.newImages, f => f === image);
    } else if (image.file) {
      _.remove(this.newImages, f => f === image);
    }
  }

  /** When an image is added or changed in the gallery dialog, change it here */
  private async fileChange(pimage: ProxImage) {
    const image = await this.makeAccountImage(pimage);

    if (image instanceof AccountImage) {
      // const pti = this.oldImages.find(pti => pti.accountImage == image);
      // if (pti) {
      //   pti.isPrimary = !!(image as ProxImage).isPrimary;
      // }
    }
    this.isChanged = true;
    return image;
  }

  /** Create a AccountImage from a ProxImage, if img is not already a AccountImage */
  private async makeAccountImage(img: ProxImage) {
    if (!img || img.entityAspect) {
      // null or already a AccountImage
      return img as AccountImage;
    }
    const buf = await ImageFns.blobToBase64(img.file);
    const si = this.dbSaveService.createEntity(AccountImage, {
      data: buf,
      description: img.fileName,
      fileName: img.fileName,
      mimeType: img.mimeType,
      name: img.fileName,
      width: img.width,
      height: img.height,
      isThumbnail: img.isThumbnail,
      accountId: this.accountId,
    });
    si['file'] = img.file;
    return si;
  }

}
